import {
    postUploadOrder,
    addToCart,
    removePartNote,
    removeProduct,
    fetchProductUpload,
} from 'helpers/api';
import { toggleLoading, formatMoney } from 'helpers/utils';
import { item } from './create-order-builder';
import { handleAddNoteToggle } from './checkout-cart';
import { updateCartTotal, getCart } from './sidecart';
import swal from 'sweetalert';
import { store } from 'js/modules/vue/store';

const state = {
    items: {},
    isFetching: false,
    didYouMean: {
        container: document.getElementsByClassName('js--did-you-mean')[0],
        list: document.getElementsByClassName('js--upload-did-you-mean')[0],
    },
    productList: document.getElementsByClassName('js--ob-cart')[0],
};

// ==================================================
// INITIALIZERS
// ==================================================

function setListeners() {
    state.didYouMean.list.addEventListener('click', ({ target }) =>
        target.classList.contains('js--didyoumean-option')
            ? handleAddDidYouMeanOption(target)
            : false,
    );

    state.productList.addEventListener('click', (e) => {
        const target =
            e.target.nodeName === 'I' && e.target.parentNode.nodeName === 'BUTTON'
                ? e.target.parentNode
                : e.target;

        const { classList } = target;

        if (classList.contains('js--add-note')) {
            return handleAddNoteToggle(e); // add note toggle event listeners
        }

        if (classList.contains('js--note-cancel')) {
            return handleAddNoteToggle(e, false); // cancel note event listeners
        }

        if (classList.contains('js--note-save')) {
            handleSaveNote(e);
            return handleAddNoteToggle(e, false); // cancel note event listeners
        }

        if (classList.contains('js--upload-add')) {
            return handleAddItemtoCart(e); // cancel note event listeners
        }

        if (classList.contains('js--ob-remove')) {
            return handleRemoveItem(e); // cancel note event listeners
        }

        return false;
    });

    document
        .getElementsByClassName('js--upload-atc')[0]
        .addEventListener('click', (e) => handleAddAllToCart(e));
}

function setItemEventListeners() {
    Array.from(document.getElementsByClassName('js--ob-li-qty')).forEach((k) => {
        if (k.getAttribute('data-events')) return;

        k.setAttribute('data-events', true);
        ['keyup', 'blur'].forEach((j) => k.addEventListener(j, (e) => handleQtyUpdate(e)));
    });
}

// ==================================================
// HANDLERS
// ==================================================

async function handleOrderUpload(e) {
    if (state.isFetching) {
        return;
    }

    const { target } = e;
    toggleLoading(true);
    state.isFetching = true;
    const { data, status } = await postUploadOrder(target.files[0]);

    if (status !== 200) {
        toggleLoading();
        state.isFetching = false;
        e.target.value = '';
    }

    if (data.InvalidList.length) {
        handleInvalidParts(data.InvalidList);
    }

    if (data.MaxItemCountMessage) {
        swal('Warning', data.MaxItemCountMessage, 'error');
    }

    if (data.DidYouMean.length) {
        handleDidYouMean(data.DidYouMean);
    }

    const html = data.Results.map((k) => {
        if (!k[0].Product.IsNLA) {
            state.items[k[0].Product.CartItemId] = {
                qty: k[0].Quantity,
                note: '',
            };
        }

        return `<li class="">${item({
            ...k[0].Product,
            qty: k[0].Quantity,
            upload: true,
            displayNoteEdit: true,
        })}</li>`;
    }).join('');

    state.productList.innerHTML = html;
    document.getElementsByClassName('js--upload-atc')[0].classList.remove('none');
    e.target.value = '';
    state.isFetching = false;
    toggleLoading();
    setItemEventListeners();
}

function handleQtyUpdate(e) {
    e.target.value = e.target.value.replace(/[^\d+$]/g, '');
    const id = e.target.getAttribute('data-id');

    if (e.keyCode === 13 || e.type === 'blur') {
        const total = e.target.closest('.js--line-item').getElementsByClassName('js--li-total')[0];

        state.items[id].qty = parseInt(e.target.value, 10);
        total.innerHTML = formatMoney(
            parseFloat(total.getAttribute('data-cost'), 2) * state.items[id].qty,
        );
    }
}

function handleSaveNote(e) {
    const noteContainer = e.target.closest('.js--li-body');
    const addNote = noteContainer.getElementsByClassName('js--add-note')[1];
    const noteValue = noteContainer.getElementsByClassName('js--upload-note')[0];
    const noteAdded = noteContainer.getElementsByClassName('js--note-added')[0];
    const note = noteContainer.getElementsByClassName('js--note-body')[0].value;
    const id = noteContainer.getAttribute('data-id');
    state.items[id].note = note;
    if (!note.length) {
        noteAdded.classList.add('none');
        addNote.classList.remove('none');
        removePartNote(id);
        return (noteValue.innerHTML = '');
    }

    addNote.classList.add('none');
    noteValue.innerHTML = note;
    noteAdded.classList.remove('none');
}

async function handleAddItemtoCart(e) {
    if (state.isFetching) {
        return;
    }

    const cartItemId = e.target.getAttribute('data-id');

    state.isFetching = true;
    toggleLoading(true);
    const res = await addToCart([{ ...state.items[cartItemId], cartItemId }]);
    updateCartTotal({ count: res.QuantityCount, total: res.CartTotal });
    getCart(res);
    e.target.closest('li').remove();
    delete state.items[cartItemId];
    toggleLoading();
    state.isFetching = false;
    store.dispatch('UpdateItemsCount');
}

async function handleAddAllToCart() {
    if (state.isFetching) {
        return;
    }

    state.isFetching = true;
    toggleLoading(true);
    const res = await addToCart(
        Object.keys(state.items).map((k) => ({
            ...state.items[k],
            cartItemId: k,
        })),
    );
    updateCartTotal({ count: res.QuantityCount, total: res.CartTotal });
    getCart(res);
    document.getElementsByClassName('js--ob-cart')[0].innerHTML = noItems();
    state.items = {};
    state.isFetching = false;
    document.getElementsByClassName('js--upload-atc')[0].classList.add('none');
    handleInvalidParts();
    toggleLoading();
}

function handleInvalidParts(parts = null) {
    const invalid = document.getElementsByClassName('js--invalid-parts')[0];

    if (!parts) {
        return invalid.classList.add('none');
    }

    invalid.getElementsByClassName('js--upload-invalid')[0].innerHTML = parts
        .map((k) => invalidPart(k))
        .join('');
    invalid.classList.remove('none');
}

function handleRemoveItem(e) {
    const target = e.target.nodeName === 'I' ? e.target.closest('button') : e.target;
    const cartItemId = target.getAttribute('data-id');

    if (!target.classList.contains('js--ob-remove')) {
        return;
    }

    removeProduct(cartItemId, 'upload-order:handleRemoveItem');
    target.closest('li').remove();
    delete state.items[cartItemId];
}

function handleDidYouMean(parts) {
    state.didYouMean.list.innerHTML = parts.map((k) => didYouMeanPart(k)).join('');
    state.didYouMean.container.classList.remove('none');
}

async function handleAddDidYouMeanOption(target) {
    if (state.isFetching) return;

    state.isFetching = true;
    const qty = parseInt(target.getAttribute('data-qty'), 10);
    const data = await fetchProductUpload(target.getAttribute('data-id'), qty);
    const row = document.createElement('li');
    const [status] = target.closest('.js--line-item').getElementsByClassName('js--upload-status');
    data.qty = qty;
    row.innerHTML = item({
        ...data,
        upload: true,
        displayNoteEdit: true,
    });

    const lineItem = state.items[data.CartItemId];

    state.items[data.CartItemId] = {
        qty: lineItem ? lineItem.qty + data.qty : data.qty,
        note: '',
    };

    const existingLineItem = Array.from(
        state.productList.getElementsByClassName('js--line-item'),
    ).filter((k) => parseInt(k.getAttribute('data-line-item'), 10) === data.Id);

    if (existingLineItem.length) {
        const qty = existingLineItem[0].getElementsByClassName('js--ob-li-qty')[0];

        if (qty) {
            qty.value = state.items[data.CartItemId].qty;
        }
    } else {
        state.productList.appendChild(row);
    }

    status.innerHTML = status.innerHTML.concat(
        `<li class="success uo__status-message">${data.qty} of ${data.PartNumber} was added to your order.</li>`,
    );
    setItemEventListeners();
    state.isFetching = false;
}

// ==================================================
// HELPERS
// ==================================================

const invalidPart = (part) => `<li class="js--line-item ob__results__item">
  <div class="ob__results__li ob--justify-start" data-label="Part">${part}</div>
</li>`;

const didYouMeanPart = (partList) => `<li class="js--line-item ob__results__item">
  <div class="ob__results__li ob--justify-start ob__results--flex2" data-label="Part">${partList[0].Product.PartNumber
    }</div>
  <div class="ob__results__li" data-label="Qty.">${partList[0].Quantity}</div>
  <div class="ob__results__li ob--justify-start ob__results--flex4" data-label="Options">${partList
        .map(({ Product, Quantity }) => didYouMeanRow({ ...Product, qty: Quantity }))
        .join('')}</div>
    <div class="ob__results__li ob--justify-start ob__results--flex3" data-label="Status"><ul class="js--upload-status"></ul></div>
</li>`;

const didYouMeanRow = ({ Id, ManufacturerCode, PartNumber, qty }) =>
    `<button class="ob__didyoumean js--didyoumean-option" data-id="${Id}" data-qty="${qty}">${ManufacturerCode} ${PartNumber}</button>`;

const noItems = () =>
    `<li class="js--ob-no-items ob__results__no-items">
    <div class="ob__results__item">
      <p>All items were added to your cart! Please upload an order to add more.</p>
    </div>
  </li>`;

// ==================================================
// INIT
// ==================================================

function init() {
    setListeners();
    document.getElementById('order').addEventListener('change', (e) => handleOrderUpload(e));
}

export default init;



// WEBPACK FOOTER //
// ./app/js/modules/upload-order.js